import { useEffect, useRef, useState } from 'react';
import type { Alert, AlertSeverity } from '../types';

/**
 * The alert strip (§2.1.6): the part that makes the Shell a working tool.
 * Status colors are the fixed status palette and never carry meaning
 * alone; every item pairs the color with a severity word and a title.
 * When nothing needs attention it says so, quietly, rather than vanishing.
 */
const SEVERITY_COLOR: Record<AlertSeverity, string> = {
  info: 'var(--ink-3)',
  warning: 'var(--status-warning)',
  serious: 'var(--status-serious)',
  critical: 'var(--status-critical)',
};

/**
 * One alert. The detail is clamped to two lines and expands on request
 * (Matthew, 4 September 2026: the dashboard reads cluttered). The
 * control only appears when the text is ACTUALLY longer than the clamp,
 * measured after layout: a "more" link that reveals nothing is noise of
 * the same kind we are removing.
 */
function AlertCard({ alert }: { alert: Alert }) {
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);
  const detail = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    const el = detail.current;
    if (!el) return;
    const measure = () => setClipped(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Re-measure on resize: two lines at one width is three at another.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [alert.detail]);

  return (
    <div className="alert cc-alert-card" style={{ ['--severity' as string]: SEVERITY_COLOR[alert.severity] }}>
      <span className="sev cc-kicker">{alert.severity}</span>
      <strong className="title">{alert.title}</strong>
      <p ref={detail} className={`detail${expanded ? '' : ' clamped'}`}>
        {alert.detail}
      </p>
      {clipped || expanded ? (
        <button type="button" className="cc-alert-more" onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'Less' : 'More'}
        </button>
      ) : null}
    </div>
  );
}

const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 3, serious: 2, warning: 1, info: 0 };

/** The one-line summary the collapsed strip shows: how many, and the worst. */
export function alertSummary(alerts: Alert[]): { count: string; worst: Alert } {
  const worst = [...alerts].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity])[0]!;
  return { count: alerts.length === 1 ? '1 thing needs attention' : `${alerts.length} things need attention`, worst };
}

/**
 * Collapsed by default (Matthew, 1 November 2026: "make the warnings
 * section on the homepage collapsible so the atlas shows on the homepage
 * immediately"): one line with the count and the most serious item, which
 * opens the full cards. Nothing is remembered between visits, so it always
 * opens folded.
 */
export function AlertStrip({ alerts }: { alerts: Alert[] | null }) {
  const [open, setOpen] = useState(false);
  if (alerts === null) return <div className="alert-quiet">Checking for anything that needs attention…</div>;
  if (alerts.length === 0) {
    return (
      <div className="alert-quiet" role="status">
        <span className="pip" aria-hidden />
        All quiet. Nothing needs attention right now.
      </div>
    );
  }
  // The mock's card treatment: an accent rule down the left edge, a
  // severity kicker in that colour, then title and detail. The colour is
  // still the fixed status palette and still never carries meaning
  // alone, because the severity word sits beside it.
  const { count, worst } = alertSummary(alerts);
  return (
    <div className="cc-alert-wrap" role="alert">
      <button
        type="button"
        className="cc-alert-toggle"
        aria-expanded={open}
        aria-controls="cc-alert-cards"
        onClick={() => setOpen((o) => !o)}
        style={{ ['--severity' as string]: SEVERITY_COLOR[worst.severity] }}
      >
        <span className="cc-alert-toggle-pip" aria-hidden />
        <strong>{count}</strong>
        <span className="cc-alert-toggle-worst">
          <span className="sev">{worst.severity}</span> {worst.title}
        </span>
        <span className="cc-alert-toggle-action">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div id="cc-alert-cards" className="alert-strip cc-alert-strip">
          {alerts.map((a) => (
            <AlertCard key={a.id} alert={a} />
          ))}
        </div>
      )}
    </div>
  );
}
